import React from 'react';
import { Button, Divider, Flex, Modal, Typography } from 'antd';

import { usePrerenderEmail, useCharbeePlanId } from '../../../hooks/usePrerenderUser';
import useQueryStringParameters from '../../../utils/useQueryStringParameters';
import { presentAsDownload } from '../../../assets/lib';
import { useEvent } from '../../events/hooks/useEvent';
import { useExportURLsAsCSVMutation, useExportURLsAsSitemapMutation, CachedCsvExportStatus } from '../CacheManagerApi';
import { CachedPagesQueryParameters, buildCachedPagesQueryParameters, hasActiveFilters } from './QueryParameters';
import { Link } from 'react-router-dom';

type ExportCachedPagesModalProps = {
  visible: boolean;
  onCancel: () => void;
};

function transformCsvExportResponse(responseStatus: CachedCsvExportStatus, email: string) {
  switch (responseStatus) {
    case 'queued':
      return email
        ? <Typography.Paragraph>We will email a download link to <strong>{email}</strong> when your export is ready. You can also access it from the <Link to="/notifications">Notifications Center</Link>.</Typography.Paragraph>
        : <Typography.Paragraph>We have scheduled your export. You will get a download link in your email once the export is ready.</Typography.Paragraph>;
    case 'not queued':
      return <Typography.Paragraph>Click start to export all your cached URLs into a CSV. Please note that exporting URLs happens in the background and you&apos;ll be notified by email once it&apos;s done.</Typography.Paragraph>;
    case 'already exists':
      return <Typography.Paragraph>CSV export has been already requested. Please try again later.</Typography.Paragraph>;
    case 'limited':
      return <Typography.Paragraph>Please upgrade your plan to use this feature.</Typography.Paragraph>;
    default:
      return <Typography.Paragraph>An error occurred while exporting URLs. Please try again later.</Typography.Paragraph>;
  }
}

function ExportCachedPagesModal({ visible, onCancel }: ExportCachedPagesModalProps) {
  const { queryStrings } = useQueryStringParameters<CachedPagesQueryParameters>(buildCachedPagesQueryParameters);
  const [exportCsv, exportCsvResult] = useExportURLsAsCSVMutation();
  const [exportSitemap, exportSitemapResult] = useExportURLsAsSitemapMutation();

  const { track } = useEvent();
  const chargebeePlanId = useCharbeePlanId();
  const email = usePrerenderEmail();

  const closeConfirmationModal = () => {
    exportCsvResult.reset();
    onCancel();
  };

  const filtersActive = hasActiveFilters(queryStrings);

  const modalTitle =
    exportCsvResult.isSuccess && exportCsvResult.data?.status === CachedCsvExportStatus.QUEUED
      ? (filtersActive ? 'Filtered URLs export scheduled' : 'All URLs export scheduled')
      : (filtersActive ? 'Export filtered URLs' : 'Export all URLs');

  const footerProp = exportCsvResult.isSuccess
    ? {
        footer: [
          <Button key="close" type="primary" onClick={closeConfirmationModal}>
            Close
          </Button>,
        ],
      }
    : { footer: null };

  const downloadCsv = () => {
    exportCsv(queryStrings);
  };

  const downloadSitemap = async () => {
    const sitemap = await exportSitemap().unwrap();
    presentAsDownload(sitemap, 'sitemap.xml');
    track('Cache URL List Exported', {
      subscription_plan: chargebeePlanId,
    });
    closeConfirmationModal();
  };

  return (
    <Modal open={visible} title={modalTitle} onCancel={onCancel} {...footerProp}>
      {exportCsvResult.isSuccess ? (
        transformCsvExportResponse(exportCsvResult.data.status, email)
      ) : (
        <div>
          <Typography.Paragraph strong>
            {filtersActive ? 'You have selected the URLs matching your current filters' : 'You have selected all URLs'}
          </Typography.Paragraph>
          {filtersActive && (
            <Typography.Paragraph type="secondary">
              The sitemap download always includes all of your cached URLs.
            </Typography.Paragraph>
          )}
          <Flex vertical>
            <Button
              type="primary"
              onClick={downloadCsv}
              loading={exportCsvResult.isLoading}
              disabled={exportSitemapResult.isLoading}
            >
              Export as CSV File
            </Button>
            <Divider plain> Or </Divider>
            <Button
              type="primary"
              onClick={downloadSitemap}
              loading={exportSitemapResult.isLoading}
              disabled={exportCsvResult.isLoading}
            >
              Download as sitemap.xml
            </Button>
          </Flex>
        </div>
      )}
    </Modal>
  );
}

export default ExportCachedPagesModal;
